import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, Navigate, useLocation, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { Button, Toggle, TextArea, Panel } from '@crazyegginc/hatch';

import { useMutation, useMetric, useAuthContext, usePermissions, useNotifications, useModal } from '/src/hooks';
import { optionsQuery } from '/src/features/options/queries';
import { updateBlockedIpsMutation, updateSiteSettingsMutation } from '/src/features/options/mutations';
import { OptionsHeader } from '/src/features/options/components/OptionsHeader';
import { CurrencySelector } from '/src/components/CurrencySelector';
import { EmailTrackModal } from '/src/features/options/components/modals/EmailTrackModal';
import { MANAGE_SUBSCRIPTION_PATH } from '/src/features/_global/constants';
import { FEATURES, CAPABILITY_REASONS, CURRENCIES } from '/src/features/_global/constants';

// eslint-disable-next-line no-restricted-syntax
export default function TrackingSettings() {
  const { site, siteId } = useOutletContext();
  const location = useLocation();
  const permissions = usePermissions();
  const notifications = useNotifications();
  const { currentAccount } = useAuthContext();
  const isStripeUser = Boolean(currentAccount?.useStripe);
  const modal = useModal();

  const trackMetric = useMetric();

  const updateSiteSettings = useMutation(updateSiteSettingsMutation);
  const { mutateAsync: asyncUpdateSiteSettings } = updateSiteSettings;
  const { data: mutationResult, isLoading: updating, mutate: updateIpList } = useMutation(updateBlockedIpsMutation);

  const { dnt, autoIdentify, defaultCurrency, explicitAgent, useSecureCookies } = site?.settings ?? {};
  const { allowed: canEdit, reason } = permissions.can('edit', FEATURES.TRACKING_OPTIONS);

  const { data } = useQuery({
    ...optionsQuery(),
    enabled: Boolean(canEdit),
  });

  const [dataInitialized, setDataInitialized] = useState(false);
  const [ipList, setIpList] = useState('loading...');
  const [savedIpList, setSavedIpList] = useState('');
  const [currency, setCurrency] = useState(() => defaultCurrency);
  const skipNextIpBlurSave = useRef(false);
  const latestSettingsRef = useRef({
    siteId,
    dnt,
    autoIdentify,
    useSecureCookies,
    explicitAgent,
    defaultCurrency,
  });

  useEffect(() => {
    const { hash } = location;

    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [location]);

  useEffect(() => {
    if (data) setDataInitialized(true);
  }, [data]);

  useEffect(() => {
    const blockedIps = data?.options?.userBlockedIps ?? '';
    setIpList(blockedIps);
    setSavedIpList(blockedIps);
  }, [data?.options?.userBlockedIps]);

  useEffect(() => {
    setCurrency(defaultCurrency);
  }, [siteId, defaultCurrency]);

  useEffect(() => {
    latestSettingsRef.current = {
      siteId,
      dnt,
      autoIdentify,
      useSecureCookies,
      explicitAgent,
      defaultCurrency,
    };
  }, [siteId, dnt, autoIdentify, useSecureCookies, explicitAgent, defaultCurrency]);

  const currentIp = data?.options?.currentIp ?? null;
  const serverIpList = savedIpList;
  const ipEntries = ipList
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean);

  const canSaveIpActions = canEdit && dataInitialized && ipList !== 'loading...';

  const canAddCurrentIp = canSaveIpActions && typeof currentIp === 'string' && currentIp.trim().length > 0;

  const hasCurrentIp = canAddCurrentIp && ipEntries.includes(currentIp);

  // Always spread the full current settings before applying a patch,
  // so a partial update never accidentally resets unspecified fields to
  // server-side defaults (which was causing explicitAgent to flip off in
  // production when email identity tracking was toggled on).
  function buildNextSettingsPayload(patch) {
    const previousSettings = { ...latestSettingsRef.current };

    const nextSettings = {
      ...latestSettingsRef.current,
      ...patch,
      siteId,
    };

    latestSettingsRef.current = nextSettings;

    return { nextSettings, previousSettings };
  }

  function updateSetting(patch) {
    const { nextSettings, previousSettings } = buildNextSettingsPayload(patch);
    updateSiteSettings.mutate(nextSettings, {
      onError: (error) => {
        latestSettingsRef.current = previousSettings;
        notifications.error({
          content: 'Failed to update settings.',
          timeout: 3000,
          context: { error },
        });
      },
    });
  }

  async function asyncUpdateSetting(patch, options) {
    const { nextSettings, previousSettings } = buildNextSettingsPayload(patch);
    return asyncUpdateSiteSettings(nextSettings, {
      ...options,
      onError: (error) => {
        latestSettingsRef.current = previousSettings;
        options?.onError?.(error);
      },
    });
  }

  function handleBlockedIpSuccess(result, fallbackIpList, successContent) {
    const errors = result?.errors ?? [];
    const hasErrors = Array.isArray(errors) && errors.length > 0;
    const canonicalIpList = result?.text ?? fallbackIpList;

    setIpList(canonicalIpList);
    setSavedIpList(canonicalIpList);

    if (hasErrors) {
      notifications.error({
        content: 'Changes failed to update',
        timeout: 3000,
        skipHoneybadger: true,
      });
      return;
    }

    notifications.success({
      content: successContent,
      timeout: 3000,
    });
  }

  function updateIps() {
    if (!canSaveIpActions) return;

    updateIpList(
      { ipList },
      {
        onError: (error) =>
          notifications.error({
            content: 'Changes failed to update',
            timeout: 5000,
            context: { error },
          }),
        onSuccess: (data) => handleBlockedIpSuccess(data?.updateBlockedIps, ipList, 'Changes saved successfully!'),
      },
    );
  }

  function addMyIp() {
    if (!canAddCurrentIp) return;
    if (updating) return; // extra click-race guard
    if (hasCurrentIp) return;

    const modifiedList =
      ipList.trim().length > 0 ? `${ipList}${ipList.endsWith('\n') ? '' : '\n'}${currentIp}\n` : `${currentIp}\n`;

    updateIpList(
      { ipList: modifiedList },
      {
        onError: (error) =>
          notifications.error({
            content: 'Changes failed to update',
            timeout: 5000,
            context: { error },
          }),
        onSuccess: (data) => handleBlockedIpSuccess(data?.updateBlockedIps, modifiedList, 'IP added to block list.'),
      },
    );
  }

  function handleIpListBlur() {
    if (skipNextIpBlurSave.current) {
      skipNextIpBlurSave.current = false;
      return;
    }

    if (!canSaveIpActions || updating || ipList === serverIpList) return;
    updateIps();
  }

  const trackEmails = useCallback(() => {
    modal.show(
      <EmailTrackModal
        onTrack={() => {
          return asyncUpdateSetting(
            { autoIdentify: !autoIdentify },
            {
              onError: (error) =>
                notifications.error({ content: 'Updating email tracking failed.', timeout: 3000, context: { error } }),
              onSuccess: () => {
                modal.close();
              },
            },
          );
        }}
      />,
    );
    // asyncUpdateSetting is reconstructed on every render so it doesn't need to
    // be listed, but all the settings it closes over do — this ensures the modal
    // always captures the latest values rather than stale ones.
  }, [modal, notifications, autoIdentify, dnt, explicitAgent, useSecureCookies, defaultCurrency, siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!site?.settings) return null;

  if (!canEdit && reason === CAPABILITY_REASONS.UPGRADE) {
    return (
      <>
        <OptionsHeader>Block specific IPs from being tracked</OptionsHeader>
        <div className="space-y-5">
          <Panel className="py-[30px]">
            <div className="text-body-2 mb-7">
              Upgrade your plan to exclude your visits or your team&apos;s visits from being tracked.
            </div>
            <Button
              component={Link}
              onMouseDown={() => trackMetric('upgrade_cta_click', 'advanced_tracking_upsell')}
              to={MANAGE_SUBSCRIPTION_PATH}
              className="self-start"
              target={isStripeUser ? '_blank' : '_self'}
              rel={isStripeUser ? 'noopener noreferrer' : undefined}
            >
              Upgrade now
            </Button>
          </Panel>
          <Panel className="py-[30px]">
            <div className="text-body-2 mb-7">
              Upgrade your plan to automatically associate emails provided in survey responses as the visitor ID.
            </div>
            <Button
              component={Link}
              onMouseDown={() => trackMetric('upgrade_cta_click', 'advanced_tracking_upsell')}
              to={MANAGE_SUBSCRIPTION_PATH}
              className="self-start"
              target={isStripeUser ? '_blank' : '_self'}
              rel={isStripeUser ? 'noopener noreferrer' : undefined}
            >
              Upgrade now
            </Button>
          </Panel>
        </div>
        <OptionsHeader className="mt-10">HTTPS-Only Cookies</OptionsHeader>
        <Panel className="py-[30px]">
          <div className="text-body-2 mb-7">Upgrade your plan to set the secure attribute on any cookies.</div>
          <Button
            component={Link}
            onMouseDown={() => trackMetric('upgrade_cta_click', 'advanced_tracking_upsell')}
            to={MANAGE_SUBSCRIPTION_PATH}
            className="self-start"
            target={isStripeUser ? '_blank' : '_self'}
            rel={isStripeUser ? 'noopener noreferrer' : undefined}
          >
            Upgrade now
          </Button>
        </Panel>
      </>
    );
  }

  if (!canEdit) {
    return <Navigate replace to="/options" />;
  }

  return (
    <>
      <div className="mb-10">
        <div id="do-not-track">
          <Panel>
            <div className="mb-5">
              <Toggle
                enabled={dnt}
                disabled={updateSiteSettings.isLoading}
                label="Ignore Do Not Track (DNT)"
                displayLabel={true}
                setEnabled={() => updateSetting({ dnt: !dnt })}
                labelClasses="!font-semibold"
              />
            </div>
            <div className="text-body-2 leading-snug">
              Crazy Egg <strong>WILL {!dnt && 'NOT'}</strong> track your website and visitors
              {dnt
                ? `, even if the visitor's browser setting has Do Not Track enabled. Your Crazy Egg data will be comparable to Google
          Analytics data.`
                : ` when the visitor's browser setting has Do Not Track enabled. Your Crazy Egg data will be different compared to Google Analytics data.`}
            </div>
          </Panel>
        </div>

        <div id="email-identity-tracking" className="pt-5">
          <Panel>
            <div className="mb-5">
              <Toggle
                enabled={autoIdentify}
                disabled={updateSiteSettings.isLoading}
                label="Automatically Track Email Addresses"
                displayLabel={true}
                setEnabled={() => (autoIdentify ? updateSetting({ autoIdentify: !autoIdentify }) : trackEmails())}
                labelClasses="!font-semibold"
              />
            </div>
            <div className="text-body-2 leading-snug">
              When Crazy Egg detects an email address entered in a form field or survey response, Crazy Egg{' '}
              <strong>WILL {!autoIdentify && 'NOT'}</strong> associate that email address as the visitor profile ID
              {autoIdentify
                ? ' (instead of a random anonymous ID).'
                : '. The visitor profile ID will show a random anonymous ID.'}
            </div>
          </Panel>
        </div>

        <div id="https-only-cookies" className="pt-5">
          <Panel>
            <div className="mb-5">
              <Toggle
                enabled={useSecureCookies}
                disabled={updateSiteSettings.isLoading}
                label="Only send cookies over HTTPS"
                displayLabel={true}
                setEnabled={() => updateSetting({ useSecureCookies: !useSecureCookies })}
                labelClasses="!font-semibold"
              />
            </div>
            <div className="text-body-2 leading-snug">
              Crazy Egg <strong>WILL {!useSecureCookies && 'NOT'}</strong> set the Secure attribute on any cookies it
              sets.{' '}
              {useSecureCookies
                ? `This will mean that Crazy Egg will only work properly on SSL-enabled pages that start with https://.`
                : 'This will allow Crazy Egg to work on both https:// and http:// pages.'}
            </div>
          </Panel>
        </div>

        <div id="bot-user-agent" className="pt-5">
          <Panel>
            <div className="mb-5">
              <Toggle
                enabled={explicitAgent}
                disabled={updateSiteSettings.isLoading}
                label="Crazy Egg Bot User Agent"
                displayLabel={true}
                setEnabled={() => updateSetting({ explicitAgent: !explicitAgent })}
                labelClasses="!font-semibold"
              />
            </div>
            <div className="text-body-2 leading-snug">
              {explicitAgent ? (
                <>
                  When enabled Crazy Egg will use the following User Agents when accessing your site:{' '}
                  <span className="rounded-sm bg-[#ededeb] px-2 py-0.5 text-xs text-[#eb5252]">Crazy Egg Bot</span> or{' '}
                  <span className="rounded-sm bg-[#ededeb] px-2 py-0.5 text-xs text-[#eb5252]">
                    Crazy Egg Bot (Mobile)
                  </span>
                  . If the default User Agent is being blocked by your site, enabling this will allow you to whitelist
                  just the Crazy Egg bot so we can capture your pages successfully. Crazy Egg&apos;s bot is on
                  Cloudflare&apos;s Verified Good Bot list. Enabling this option allows Cloudflare to correctly identify
                  and allow Crazy Egg to access your site.
                </>
              ) : (
                <>
                  When disabled Crazy Egg will use a common Chrome User Agent for a desktop, mobile, or tablet device by
                  default. This is less likely to get blocked by automated website software, but it&apos;s not
                  recommended if you use Cloudflare. If you use Cloudflare you should enable this option to take
                  advantage of Cloudflare&apos;s Verified Good Bot list.
                </>
              )}
            </div>
          </Panel>
        </div>
      </div>

      <div id="block-ips">
        <OptionsHeader>Block specific IPs from being tracked for all sites</OptionsHeader>
        <Panel className="mb-[50px]">
          <div className="text-body-2 mb-5 leading-snug pr-2.5">
            All Heatmaps, Recordings, A/B Tests, and Surveys will ignore all visits and clicks from any of these
            addresses listed here. This means that Crazy Egg will exclude these IP addresses when collecting Heatmaps,
            Recordings, & A/B Testing data, so you can omit traffic generated by your internal team. A/B Tests and
            Surveys will not be displayed to any visitors from these IPs. Add the IP addresses below.
          </div>
          <div className="flex">
            <div className="w-[472px]">
              <TextArea
                resizeable="none"
                rows={6}
                value={ipList}
                onChange={(e) => setIpList(e.target.value)}
                onBlur={handleIpListBlur}
                disabled={!dataInitialized}
              />
            </div>
            <div className="ml-3">
              <div className="text-header-4 mb-2.5">Examples:</div>
              <ul className="text-body-2 mb-5 space-y-2">
                <li>127.0.0.1</li>
                <li>127.0.0.*</li>
                <li>127.0.0.0-127.0.2.0</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col">
            {mutationResult?.updateBlockedIps?.errors?.map((error, idx) => {
              if (error?.line) {
                return (
                  <div className="text-error my-1.25" key={`BlockedIpError${idx}`}>
                    <strong>Line {error.line}:</strong> {error.message}
                  </div>
                );
              }
              return (
                <div className="text-error my-1.25" key={`BlockedIpError${idx}`}>
                  {error?.message}
                </div>
              );
            })}
          </div>

          {canAddCurrentIp && !hasCurrentIp ? (
            <div className="text-body-2 mt-2 mb-1">
              <Button
                variant="ghost-primary"
                onMouseDown={() => {
                  skipNextIpBlurSave.current = true;
                }}
                onFocus={() => {
                  skipNextIpBlurSave.current = true;
                }}
                onClick={addMyIp}
                disabled={!canAddCurrentIp}
                className={classNames('!pl-0', {
                  'bg-transparent': !canAddCurrentIp,
                })}
              >
                Add my IP address {currentIp} to the block list
              </Button>
            </div>
          ) : null}
        </Panel>
      </div>

      <div id="currency">
        <OptionsHeader>Currency settings</OptionsHeader>
        <Panel className="mb-[50px]">
          <div className="text-body-2 pr-2.5 leading-snug">
            <p className="mb-2.5">
              Set the default currency for your user sessions. This will only affect sessions where a currency is
              unspecified. For example, if you use Shopify, your sessions will have a currency specified by your Shopify
              store.
            </p>
            <div className="w-1/2">
              <CurrencySelector
                value={currency || CURRENCIES.USD}
                onSelect={(defaultCurrency) => {
                  setCurrency(defaultCurrency);
                  updateSetting({ defaultCurrency });
                }}
                container="#ce_app"
                popperClassName="[&>*[role='listbox']]:!max-h-[120px]"
              />
            </div>
          </div>
        </Panel>
      </div>
    </>
  );
}
